import { useIsMutating } from "@tanstack/react-query";

import { CLAIM_TERMINAL_MUTATION_KEY } from "services/dbApi/hooks/useClaimTerminalTaskMutation";
import { PATCH_TASK_MUTATION_KEY } from "services/dbApi/hooks/usePatchTaskMutation";
import { POST_TASK_MUTATION_KEY } from "services/dbApi/hooks/usePostTaskMutation";

/**
 * Returns `true` while any task write — POST, PATCH, or the terminal claim — is
 * in flight.
 *
 * The focus-refetch effects gate on this: a focus refetch of the tasks list
 * that lands mid-write can resolve with pre-commit server state and clobber a
 * just-registered or just-dismissed row, so they skip the refetch while a write
 * is outstanding (ADR 056).
 *
 * The claim belongs here for the same reason: a GET resolving between the claim
 * request and its response carries the row as it was before the claim, so its
 * `terminalDirectiveInjectedAt` is still null. Dropping the won claim from cache
 * also drops the row out of stranded-directive recovery, which acts only on a
 * row whose flag is set.
 */
export function useTaskWriteInFlight(): boolean {
  const patchInFlight = useIsMutating({ mutationKey: PATCH_TASK_MUTATION_KEY });
  const postInFlight = useIsMutating({ mutationKey: POST_TASK_MUTATION_KEY });
  const claimInFlight = useIsMutating({
    mutationKey: CLAIM_TERMINAL_MUTATION_KEY,
  });
  return patchInFlight > 0 || postInFlight > 0 || claimInFlight > 0;
}
